/**
 * User-profile service — the Firestore side of authentication.
 *
 * Firebase Auth owns credentials (email/password); Firestore owns the
 * application profile, most importantly the user's *role*. Each auth user
 * has a matching document at `users/{uid}`:
 *
 *   {
 *     uid, email, displayName, role,
 *     createdAt, updatedAt        // server timestamps
 *   }
 *
 * Route guards resolve a user's role from this document, so it is the
 * security-critical record. Firestore rules (see setup notes) must prevent
 * users from escalating their own `role`.
 */
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

import { db } from './config';

const USERS = 'users';

export const usersService = {
  /**
   * Fetch a profile by uid, or null if it doesn't exist. A signed-in user with
   * no profile is UNPROVISIONED — profiles are created only by an admin (server
   * side, via the `adminAccounts` Cloud Function), so the client never creates
   * one. `AuthContext` signs out any session that returns null here.
   */
  async get(uid) {
    const snapshot = await getDoc(doc(db, USERS, uid));
    return snapshot.exists() ? { uid, ...snapshot.data() } : null;
  },

  /** Patch a profile (e.g. the user updating their own timezone). */
  async update(uid, patch) {
    await updateDoc(doc(db, USERS, uid), { ...patch, updatedAt: serverTimestamp() });
  },

  /**
   * Stamp that the user changed their own password (via the in-app Change
   * Password flow). The admin Accounts page reads this so an in-app change shows
   * as "Changed by user" — the reset-link flow is detected separately from the
   * auth token time, which a client-side `updatePassword` doesn't bump.
   *
   * Only the timestamp is recorded. The new password is chosen in the browser
   * (or on Google's reset page) and is never sent to us in a readable form.
   */
  async markPasswordChanged(uid) {
    await updateDoc(doc(db, USERS, uid), {
      credentialStatus: 'user-set',
      passwordChangedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  },
};

export default usersService;
